import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useSnackbar } from 'notistack';
import { Box } from '@material-ui/core';
import PlaylistAddOutlinedIcon from '@material-ui/icons/PlaylistAddOutlined';
import SyncIcon from '@material-ui/icons/Sync';
import { useAppContext } from 'contexts/AppContext';
import { wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormGridWrapper from 'ui-elements/form-elements/FsFormGridWrapper';
import FsFormGridItem from 'ui-elements/form-elements/FsFormGridItem';
import FsConfirmationDialog from 'ui-elements/modals/FsConfirmationDialog';
import FsButton from 'ui-elements/buttons/FsButton';
import FsBody from 'ui-elements/typography/FsBody';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import { getSnapshot } from 'mobx-state-tree';
import { WebOriginSingleRowProps } from './WebOriginSingleRow/WebOriginSingleRow';
import WebOriginSingleRow from './WebOriginSingleRow';
import { AddNewButton } from './FsWebOriginForm.style-elements';

const FsWebOriginForm: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    redirectsWeb: {
      data: redirectsWeb,
      getRedirects: getRedirectsWeb,
      init: initRedirectsWeb,
      update: refreshRedirectsWeb,
      addRedirect: addRedirectWeb,
      removeRedirect: removeRedirectWeb,
      updateRedirect: updateRedirectWeb,
    },
  } = useAppContext();

  const rows = getRedirectsWeb();
  const [pending, setPending] = useState<boolean>(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(
    false
  );
  const suspiciousRedirects = rows.filter(
    ({ origin }) =>
      !!origin && !origin.includes('http://') && !origin.includes('https://')
  );

  const handleAdd = () => {
    addRedirectWeb();
  };

  const handleRemove = (selectedId: number) => {
    removeRedirectWeb(selectedId);
  };

  const handleChange = (changedField: WebOriginSingleRowProps) => {
    updateRedirectWeb(changedField);
  };

  const getCleanListOfRedirects = () => {
    return getSnapshot(redirectsWeb).filter(
      ({ origin }) =>
        !!origin && (origin.includes('http://') || origin.includes('https://'))
    );
  };

  const handleConfirm = async () => {
    setShowConfirmationDialog(false);
    setPending(true);
    await wait(1000);
    setPending(false);
    const payload = getCleanListOfRedirects();
    // eslint-disable-next-line no-console
    console.log('UPDATE:', payload);
    enqueueSnackbar(defaultLocales.updateOriginNotificationSuccess, {
      variant: 'success',
    });
    refreshRedirectsWeb();
  };

  const handleUpdate = async () => {
    setPending(true);
    if (suspiciousRedirects.length > 0) {
      setShowConfirmationDialog(true);
      setPending(false);
      return;
    }
    await handleConfirm();
  };

  useEffect(() => {
    initRedirectsWeb();
  }, [initRedirectsWeb]);

  return (
    <Box maxWidth={600}>
      <Box marginBottom={2}>
        <Box>
          {/* <pre>{JSON.stringify(rows, null, 2)}</pre> */}
          {rows.map(({ id, ...otherProps }) => (
            <WebOriginSingleRow
              {...otherProps}
              key={id}
              id={id}
              isOnlyOneRow={rows.length <= 1}
              onRemove={() => handleRemove(id)}
              onChange={(changedField) => handleChange({ ...changedField })}
            />
          ))}
          <FsFormGridWrapper>
            <FsFormGridItem>
              <AddNewButton
                fullWidth
                color="primary"
                startIcon={<PlaylistAddOutlinedIcon />}
                onClick={handleAdd}
              >
                {defaultLocales.addOrigin}
              </AddNewButton>
            </FsFormGridItem>
            <FsFormGridItem style={{ flexGrow: 0, minWidth: 48 }} />
          </FsFormGridWrapper>
        </Box>
      </Box>
      <FsPendingButton
        pending={pending}
        onClick={handleUpdate}
        startIcon={<SyncIcon />}
      >
        {defaultLocales.update}
      </FsPendingButton>

      <FsConfirmationDialog
        title={defaultLocales.confirmInvitations}
        showModal={showConfirmationDialog}
        setShowModal={() => setShowConfirmationDialog(false)}
        actionButtons={[
          <FsButton key={1} onClick={handleConfirm}>
            {defaultLocales.yes}, {defaultLocales.continue.toLowerCase()}
          </FsButton>,
          <FsButton
            key={2}
            onClick={() => setShowConfirmationDialog(false)}
            color="primary"
            variant="contained"
          >
            {defaultLocales.no}
          </FsButton>,
        ]}
      >
        <FsBody>{defaultLocales.sendRedirectsInvalidUrlQuestion}</FsBody>
        <Box mt={2}>
          <FsBodyBold>{defaultLocales.suspiciousRedirects}:</FsBodyBold>
          {suspiciousRedirects.map(({ id, origin }) => (
            <FsBody key={id}> - {origin}</FsBody>
          ))}
        </Box>
      </FsConfirmationDialog>
    </Box>
  );
};

export default observer(FsWebOriginForm);
